/**
 * canvas-engine/renderer/elements/elementAnimation.js
 *
 * OWN tiny, self-contained entrance-animation resolver for the Canvas Engine
 * element wrapper.
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/...` or
 * `academy-ui/src/template/`. academy-ui cannot import admin-only modules and
 * the Canvas Engine is mirrored verbatim into academy-ui, so this mirrors the
 * shared `Animation_Config` contract (academy-ui/src/template/animationConfig.js)
 * inline rather than importing it.
 *
 * The CSS `animation-name` values returned here MATCH the reader keyframes ids
 * registered by `academy-ui/src/template/templateAnimations.jsx`
 * (`avps-anim-fade`, `avps-anim-slide-in`, `avps-anim-slide-up`,
 * `avps-anim-slide-down`, `avps-anim-slide-left`, `avps-anim-slide-right`,
 * `avps-anim-zoom`). When the reader mounts those `@keyframes` (via
 * `AnimationKeyframes`), they drive the per-element entrance animation produced
 * by this helper.
 *
 * Pure functions, no React, no side effects. Used by `CanvasRenderer` to merge
 * an additive entrance-animation inline style onto the element wrapper box.
 *
 * Identity rule: an absent config or `type === 'none'` produces NO animation
 * (returns `null`), so an element without an animation renders exactly as it
 * does today. (Req 6.7, 6.8, 6.9)
 *
 * Robustness rule: the Canvas Engine renderer is a pure presentational
 * component with no IntersectionObserver wiring. To guarantee content is NEVER
 * left hidden, both `trigger: 'load'` and `trigger: 'scroll'` resolve to the
 * played end-state (animation runs immediately). The reader's `AnimatedRegion`
 * provides true scroll gating for section/chrome targets; here we prefer a
 * simple, robust, always-visible result.
 */

/** Default Animation_Config (mirrors the shared contract). */
const ANIMATION_DEFAULTS = Object.freeze({
  type: 'none',
  trigger: 'load',
  duration: 600,
  delay: 0,
  easing: 'ease',
});

/** Base class used to derive the keyframes id (`avps-anim-<type>`). */
const ANIMATION_BASE_CLASS = 'avps-anim';

/** Allowed animation types (the first, `none`, means "no animation"). */
const ANIMATION_TYPES = Object.freeze([
  'none',
  'fade',
  'slide-in',
  'slide-up',
  'slide-down',
  'slide-left',
  'slide-right',
  'zoom',
]);

/** Allowed animation triggers. */
const ANIMATION_TRIGGERS = Object.freeze(['load', 'scroll']);

/** Upper bounds keep persisted values sane and prevent runaway animations. */
const MAX_DURATION_MS = 60000;
const MAX_DELAY_MS = 60000;
const MAX_EASING_LENGTH = 64;

/**
 * Map an animation type to its keyframes id. These ids MUST match the reader's
 * registered `@keyframes` ids (e.g. `avps-anim-fade`).
 * @param {string} type
 * @returns {string|null} keyframes id, or null for `none`/unknown types
 */
function keyframesIdForType(type) {
  if (!type || type === 'none' || !ANIMATION_TYPES.includes(type)) return null;
  return `${ANIMATION_BASE_CLASS}-${type}`;
}

function clampNonNegative(value, fallback, max) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return fallback;
  return Math.min(numeric, max);
}

function sanitizeEasing(value) {
  if (typeof value !== 'string') return ANIMATION_DEFAULTS.easing;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_EASING_LENGTH) return ANIMATION_DEFAULTS.easing;
  // Reject characters that could break out of the CSS value context.
  if (/[<>{};]/.test(trimmed)) return ANIMATION_DEFAULTS.easing;
  return trimmed;
}

/**
 * Merge an arbitrary persisted value over the defaults, validating each field.
 * Unknown `type`/`trigger` fall back to defaults; `duration`/`delay` are clamped
 * to finite, non-negative, bounded numbers; `easing` is sanitized.
 * @param {object|null|undefined} config
 * @returns {{type:string,trigger:string,duration:number,delay:number,easing:string}}
 */
function normalizeAnimationConfig(config) {
  if (!config || typeof config !== 'object') {
    return { ...ANIMATION_DEFAULTS };
  }
  const type = ANIMATION_TYPES.includes(config.type) ? config.type : ANIMATION_DEFAULTS.type;
  const trigger = ANIMATION_TRIGGERS.includes(config.trigger)
    ? config.trigger
    : ANIMATION_DEFAULTS.trigger;
  return {
    type,
    trigger,
    duration: clampNonNegative(config.duration, ANIMATION_DEFAULTS.duration, MAX_DURATION_MS),
    delay: clampNonNegative(config.delay, ANIMATION_DEFAULTS.delay, MAX_DELAY_MS),
    easing: sanitizeEasing(config.easing),
  };
}

/**
 * Resolve an element `Animation_Config` into an additive inline-style descriptor
 * for the element wrapper box.
 *
 * Returns `null` (identity — no animation) when the config is absent or its
 * normalized type is `none`.
 *
 * The descriptor sets `animation-name` (matching the reader keyframes id),
 * `-duration`, `-delay`, `-timing-function`, and `animation-fill-mode: both` so
 * the element holds its end-state after playing. Both `load` and `scroll`
 * triggers play immediately (running) — the engine has no observer, and this
 * guarantees the element is never left hidden.
 *
 * @param {object|null|undefined} config a normalized/raw Animation_Config
 * @returns {object|null} an inline CSS style fragment, or `null` for no animation
 */
export function elementAnimationStyle(config) {
  if (!config || typeof config !== 'object') return null;
  const normalized = normalizeAnimationConfig(config);
  if (normalized.type === 'none') return null;

  const keyframesId = keyframesIdForType(normalized.type);
  if (!keyframesId) return null;

  return {
    animationName: keyframesId,
    animationDuration: `${normalized.duration}ms`,
    animationDelay: `${normalized.delay}ms`,
    animationTimingFunction: normalized.easing,
    animationFillMode: 'both',
    animationPlayState: 'running',
  };
}

export default elementAnimationStyle;
